import { forwardRef } from "react";
import { AppState, Platform } from "react-native";
import {
  KeyboardAvoidingView,
  KeyboardAwareScrollView,
  KeyboardController,
} from "react-native-keyboard-controller";
import type {
  KeyboardAvoidingViewProps,
  KeyboardAwareScrollViewProps,
  KeyboardAwareScrollViewRef,
} from "react-native-keyboard-controller";

/**
 * App-wide keyboard handling.
 *
 * Every screen with a `TextInput` must use one of the two components below so
 * the keyboard behaves identically everywhere. Do not hand-roll a
 * `KeyboardAvoidingView` with a magic `keyboardVerticalOffset` — that is what
 * caused headers to be pushed off-screen on some screens and inputs to be
 * covered on others.
 *
 * Pick by layout:
 *
 * - Scrollable form / content screen ..... `KeyboardScrollView`
 * - Pinned bottom composer or sheet ...... `KeyboardAvoidingContainer`
 */

/** Dragging the list down dismisses the keyboard, natively on each platform. */
const DISMISS_MODE = Platform.OS === "ios" ? "interactive" : "on-drag";

/** Breathing room between the caret and the top of the keyboard. */
const BOTTOM_OFFSET = 16;

export type KeyboardScrollViewProps = KeyboardAwareScrollViewProps;

/**
 * Scrollable container that keeps the focused `TextInput` above the keyboard.
 *
 * Use this in place of `ScrollView` on any screen that has text inputs. It
 * scrolls the focused input into view rather than shifting the whole screen, so
 * it stays correct no matter what is rendered above it (headers, banners, tab
 * bars) and needs no per-screen offset tuning.
 */
export const KeyboardScrollView = forwardRef<
  KeyboardAwareScrollViewRef,
  KeyboardScrollViewProps
>(function KeyboardScrollView(
  {
    bottomOffset = BOTTOM_OFFSET,
    keyboardDismissMode = DISMISS_MODE,
    keyboardShouldPersistTaps = "handled",
    ...rest
  },
  ref,
) {
  return (
    <KeyboardAwareScrollView
      ref={ref}
      bottomOffset={bottomOffset}
      keyboardDismissMode={keyboardDismissMode}
      keyboardShouldPersistTaps={keyboardShouldPersistTaps}
      {...rest}
    />
  );
});

export type KeyboardAvoidingContainerProps = KeyboardAvoidingViewProps;

/**
 * Container that lifts a pinned bottom element (chat composer, sheet footer)
 * clear of the keyboard.
 *
 * `automaticOffset` makes the view measure its own position on screen, so it
 * compensates for whatever sits above it — a header, a banner, a modal
 * backdrop — instead of relying on a hardcoded `keyboardVerticalOffset`.
 * `keyboardVerticalOffset` is still available here, but it is purely additive
 * padding on top of the measured offset.
 */
export const KeyboardAvoidingContainer = forwardRef<
  React.ComponentRef<typeof KeyboardAvoidingView>,
  KeyboardAvoidingContainerProps
>(function KeyboardAvoidingContainer(
  { behavior = "padding", keyboardVerticalOffset = 0, ...rest },
  ref,
) {
  return (
    <KeyboardAvoidingView
      ref={ref}
      automaticOffset
      behavior={behavior}
      keyboardVerticalOffset={keyboardVerticalOffset}
      {...rest}
    />
  );
});

/**
 * Let go of the keyboard when the app leaves the foreground.
 *
 * NOT a fix for the stranded-composer fault, and the device proved it. The
 * fault (builds 14/16/17): lock mid-typing, unlock — keyboard gone, its space
 * still reserved, composer stranded until a tap self-heals it. The theory was
 * a hide event lost during lock leaving the library believing a keyboard is
 * open; the build-17 probe refuted it twice over — isVisible() reads 0
 * through the whole failure, and the composer is at REST at resume, rising
 * 300 px over the following 600 ms. Nothing is carried across the background
 * transition; the lift is produced AFTER the app is back. The call stays
 * because build 14 showed the fault WITHOUT it (0c19a99a shipped in 15), so
 * it neither creates nor clears the state, and background-with-no-focus is
 * the wanted state regardless. use-keyboard-probe.ts carries the measurement
 * and the two candidate fixes it discriminates between. Builds 15 and 16
 * shipped IDENTICAL keyboard code — build 15 was the race landing right five
 * times, not a fix.
 *
 * Only `background` counts: on iOS `inactive` also fires for the shade,
 * Control Centre and the app-switcher peek, and dismissing there snatches
 * the keyboard mid-sentence (use-voice-mode.ts learned this the hard way).
 * Installed ONCE at the app root — one listener per composer screen would
 * dismiss several times over.
 *
 * ANIMATED on purpose — `animated: false` selects a DIFFERENT native path
 * (KeyboardControllerModuleImpl.kt:53): a control request that throws while
 * the system is already animating the IME away (exactly what a screen lock
 * does) and is silently cancelled when the window cannot take IME control —
 * so nothing hides, clearFocus never runs, no insets callback fires, and the
 * interpolate()-driven padding stays stuck: the reported fault, manufactured.
 * The animated branch is two synchronous UI-thread calls with an
 * unconditional clearFocus(), which also stops Android restoring the IME on
 * unlock.
 */
export function installKeyboardBackgroundReset(): () => void {
  if (Platform.OS === "web") return () => {};

  const subscription = AppState.addEventListener("change", (next) => {
    if (next !== "background") return;
    // Best-effort — a rejection here must never surface, since the worst case
    // is the stale offset we already had.
    void KeyboardController.dismiss().catch(() => {});
  });

  return () => subscription.remove();
}
